import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Fieldset, Loader, Stepper } from "@mantine/core";
import { CalendarBlankIcon, EnvelopeIcon, PhoneIcon, ShieldCheckIcon, TruckIcon, UserIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { ComponentProps, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm, useFormContext, UseFormProps } from "react-hook-form";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { EventListener } from "react-lottie";
import { toast } from "react-toastify";
import { z } from "zod";

import { LErrorIcon, LSuccessIcon } from "@/assets/lottie";
import { Block } from "@/components/@custom/Block";
import { Fields } from "@/components/@custom/Fields";
import { Input as CustomInput } from "@/components/@custom/Input";
import { SideBySide } from "@/components/@custom/SideBySide";
import { IMPLEMENT_TYPES } from "@/constants";
import { useWindowLocationHost } from "@/hooks/useWindowLocationHost";
import { DATE_PATTERNS } from "@/lib/Mantine";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import api from "@/services/api";
import { truckService } from "@/services/truck/index";
import { cn } from "@/utils/cn";
import { crypto } from "@/utils/cript";
import { cva } from "@/utils/cva.config";
import { transformToOption } from "@/utils/transformToOption";

interface Company {
  id: number;
  logo: string;
  name: string;
}

type ImplementType = typeof IMPLEMENT_TYPES;

type Schema = z.infer<typeof schema>;
const schema = z.object({
  user: z.object({
    name: GLOBAL_SCHEMA.NAME.max(80, "Tamanho máximo de 80 caracteres"),
    cpf: GLOBAL_SCHEMA.CPF,
    cnh: GLOBAL_SCHEMA.CNH,
    birthDate: GLOBAL_SCHEMA.BIRTHDAY,
    phone: GLOBAL_SCHEMA.PHONE,
    email: GLOBAL_SCHEMA.EMAIL
  }),
  truck: GLOBAL_SCHEMA.TRUCK
});

export const Route = createFileRoute("/_auth/app/registro/$id")({
  params: {
    parse: ({ id }) => {
      return { id: +id };
    }
  },
  component: RegisterDriver,
  head: () => ({
    meta: [{ title: "Registro - App" }]
  })
});

function RegisterDriver() {
  const { id } = Route.useParams();

  const [activeStep, setActiveStep] = useState(0);

  const [brands, setBrands] = useState({ data: [{ value: "", label: "" }], isLoading: false, isError: false });
  const [models, setModels] = useState({ data: [{ value: "", label: "" }], isLoading: false, isError: false });
  const [submitStatus, setSubmitStatus] = useState({ success: false, error: false });

  const [implementsTypes, setimplementsTypes] = useState<OptionData[]>([{ value: "", label: "" }]);
  const [company, setCompany] = useState<Company>({} as Company);

  const FORM_PROPS: UseFormProps<Schema> = {
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      user: {
        name: "",
        cpf: "",
        cnh: "",
        // birthDate: "02/06/2007",
        birthDate: dayjs().subtract(18, "years").format(DATE_PATTERNS.BR.DATE),
        phone: "",
        email: ""
      },
      truck: {
        // plate: "OTU2018",
        // renavam: "010210619-61",
        plate: "",
        renavam: "",
        brand: "",
        model: "",
        color: "",
        // implementType: "BITREM 7 EIXOS",
        implementType: "",
        shaftQuantity: 0,
        grossCapacity: 0,
        netCapacity: 0
      }
    }
  };
  const methods = useForm<Schema>(FORM_PROPS);

  const brand = methods.watch("truck.brand");
  const implementType = methods.watch("truck.implementType") as ImplementType[number]["type"];
  const grossCapacity = methods.watch("truck.grossCapacity");
  const shaftQuantity = methods.watch("truck.shaftQuantity");
  const netCapacity = methods.watch("truck.netCapacity");

  const valuesForm = methods.getValues();

  const modelLabel = useMemo(() => getLabelelByValue({ arr: models.data, value: valuesForm.truck.model }), [valuesForm.truck.model]);
  const brandLabel = useMemo(() => getLabelelByValue({ arr: brands.data, value: valuesForm.truck.brand }), [valuesForm.truck.brand]);

  const companyLogo = company?.logo && `${import.meta.env.VITE_APP_API_URL}/${company?.logo}`;

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { user, truck } = data;

    const [error, success] = await truckService.query.getIsTruck(truck.plate);

    if (!success && error) {
      setActiveStep(1);
      methods.setFocus("truck.plate");
      methods.setError("truck.plate", { message: "Placa inválida" });
      return;
    }

    const driverId = sessionStorage.getItem("@App:driverId");

    const body: NextApp.TruckRebindBody = {
      driver: {
        id: NaN,
        name: user.name.toUpperCase(),
        cpf: user.cpf,
        cnh: user.cnh,
        isEnabled: true,
        shippingCompany: null,
        birthDate: user.birthDate,
        contact: [
          {
            id: null,
            name: user.name.toUpperCase(),
            fone: user.phone,
            email: user.email
          }
        ],
        company: id ? { id } : null
      },
      truck: {
        driver: {
          id: Number(driverId)
        },
        licensePlate: truck.plate,
        renavam: truck.renavam,
        vehicleModel: {
          id: truck.model,
          truckBrand: {
            id: truck.brand
          }
        },
        color: truck.color,
        company: {
          id: id ? id : null
        },
        owner: user.name.toUpperCase(),
        implementType: truck.implementType,
        grossCapacity: truck.grossCapacity,
        shaftQuantity: truck.shaftQuantity,
        netCapacity: truck.netCapacity
      }
    };

    if (driverId) {
      delete body?.driver;
    }

    nextStep();

    const [isError] = await truckService.mutation.patchTruckRebind(body, {
      onError({ message }) {
        if (message.includes(`Esse renavam não corresponde a placa ${methods.getValues("truck.plate")}!`)) {
          setActiveStep(1);
          methods.setValue("truck.renavam", "");
          methods.trigger("truck.renavam");
        }
      }
    });

    if (isError) {
      setSubmitStatus((prev) => ({ ...prev, error: true }));
      return;
    }

    setSubmitStatus((prev) => ({ ...prev, success: true }));
  };

  const onInvalid: SubmitErrorHandler<Schema> = (_err) => {
    toast.error("Alguns campos não estão devidamente preenchidos!");
  };

  function getLabelelByValue<T extends { value: string; label: string }>({ arr = [], value }: { arr: T[]; value: string }) {
    const found = arr.find((item) => item.value === value);
    if (!found || arr.length === 0) {
      console.error("RegisterDriver::getLabelelByValue::ERROR: Modelo não encontrado!");
      return "";
    }

    return found.label;
  }

  function fetchOptions<T extends "brands" | "models">(type: T, brandId?: T extends "models" ? string : undefined) {
    const matcher = {
      brands: async () => {
        setBrands((prev) => ({ ...prev, isLoading: true }));
        const [error, data] = await truckService.query.getBrands();
        const formattedArr = transformToOption(
          data!,
          (item) => item.id,
          (item) => item.name
        );
        setBrands((prev) => ({ ...prev, data: formattedArr, isLoading: false, isError: error }));
      },
      models: async () => {
        if (!brandId) return console.error("Brand ID is required to fetch models");
        setModels((prev) => ({ ...prev, isLoading: true }));
        const [error, data] = await truckService.query.getModelsByBrand(brandId);
        const formattedArr = transformToOption(
          data!,
          (item) => item.id,
          (item) => item.name
        );
        setModels((prev) => ({ ...prev, data: formattedArr, isLoading: false, isError: error }));
      }
    };

    if (!(type in matcher)) return console.error(`Invalid type: ${type}. Expected ${Object.keys(matcher).join(", ")}.`);

    matcher[type]();
  }

  const stepFields = ["user", "truck"] as const;
  const validateCurrentStep = async () => {
    if (activeStep >= stepFields.length) return true;

    const isValid = await methods.trigger(stepFields[activeStep]);
    return isValid;
  };
  const nextStep = async () => {
    const isValid = await validateCurrentStep();

    if (!isValid) {
      toast.error("Pra avançar para o próximo passo, preencha os campos corretamente!");
      return;
    }
    setActiveStep((prev) => (prev < 3 ? prev + 1 : prev));
  };
  const prevStep = () => setActiveStep((prev) => (prev > 0 ? prev - 1 : prev));

  const calledBrand = useRef(false);
  useEffect(() => {
    if (calledBrand.current) return;

    if (activeStep === 1) {
      calledBrand.current = true;
      fetchOptions("brands");
    }
  }, [activeStep]);

  useEffect(() => {
    if (!brand) return;

    setModels((prev) => ({ ...prev, data: [{ value: "", label: "" }] }));
    methods.trigger("truck.model");
    methods.setFocus("truck.model");
    methods.setValue("truck.model", "");

    fetchOptions("models", brand);
  }, [brand]);

  useEffect(() => {
    if (!!grossCapacity || !!shaftQuantity || !!netCapacity) return;
    function findImplementType(shaftQuantity: number, grossCapacity: number, netCapacity: number): ImplementType[number]["type"] | undefined {
      const found = IMPLEMENT_TYPES.find((item) => item.shaftQuantity === shaftQuantity && item.grossCapacity === grossCapacity && item.netCapacity === netCapacity);
      return found?.type;
    }

    const found = findImplementType(shaftQuantity, grossCapacity, netCapacity);
    if (!found) return;
    methods.setValue("truck.implementType", found);
    methods.clearErrors("truck.implementType");
  }, [grossCapacity, shaftQuantity, netCapacity]);

  useEffect(() => {
    if (!implementType) return;

    const findInplementType = (type: ImplementType[number]["type"]) => {
      return IMPLEMENT_TYPES.find((item) => item.type === type);
    };

    const found = findInplementType(implementType);
    if (!found) return;
    methods.setValue("truck.shaftQuantity", found.shaftQuantity);
    methods.setValue("truck.grossCapacity", found.grossCapacity);
    methods.setValue("truck.netCapacity", found.netCapacity);
  }, [implementType]);

  // const [modalPlate, setModalPlate] = useState(false);
  // useEffect(() => {
  //   if (modalPlate) {
  //     modals.openContextModal({
  //       modal: "plateNotValid",
  //       withCloseButton: false,
  //       innerProps: {
  //         plate: "ABC1234"
  //       }
  //     });
  //   }
  // }, [modalPlate]);

  /* ############################################################ */

  const { windowLocationHost } = useWindowLocationHost();

  useEffect(() => {
    const types = IMPLEMENT_TYPES.map((item) => {
      return { value: item.type, label: item.type };
    });
    setimplementsTypes(types);

    api
      .get(`/company/host/`, {
        params: { host: windowLocationHost }
      })
      .then((response) => {
        setCompany(response.data);
      })
      .catch((error) => {
        console.error("Error fetching company data:", error);
      });
  }, [windowLocationHost]);

  return (
    <>
      <SideBySide.Primary>
        {companyLogo && <img src={companyLogo} className={hiddenOnMobile({ className: "mb-2 w-[300px]" })} />}

        <Block.Form className="w-[min(100%_,_var(--container-xl))]">
          <Block.FormTitle className="block md:hidden">Cadastro de Motorista</Block.FormTitle>

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
              <Stepper active={activeStep} onStepClick={setActiveStep} size="sm" classNames={{ steps: "!hidden md:!flex " }} wrap={false}>
                <Stepper.Step
                  label="Primeiro passo"
                  description="Inserir dados pessoais"
                  icon={<UserIcon weight="bold" />}
                  color={methods.getFieldState("user").invalid ? "red" : ""}
                >
                  <Step.One />
                </Stepper.Step>

                <Stepper.Step
                  label="Segundo passo"
                  description="Inserir dados do veículo"
                  icon={<TruckIcon weight="bold" />}
                  color={methods.getFieldState("truck").invalid ? "red" : ""}
                  loading={brands.isLoading}
                >
                  <Step.Two brands={brands} models={models} implementsTypes={implementsTypes} />
                </Stepper.Step>

                <Stepper.Step label="Terceiro passo" description="Revisar dados" icon={<ShieldCheckIcon weight="bold" />} loading={methods.formState.isSubmitting}>
                  <Step.Three brandLabel={brandLabel} modelLabel={modelLabel} />
                </Stepper.Step>

                <Stepper.Completed>
                  <Step.Four success={submitStatus.success} error={submitStatus.error} />
                </Stepper.Completed>
              </Stepper>

              <div className="my-3 flex justify-between">
                {(activeStep !== 0 && activeStep !== 3) || submitStatus.error ? (
                  <Button variant="transparent" onClick={prevStep} leftSection={<MdKeyboardArrowLeft />} size="sm">
                    Voltar
                  </Button>
                ) : null}

                {activeStep < 2 && (
                  <Button onClick={nextStep} rightSection={<MdKeyboardArrowRight />} className={cn([activeStep === 0 && "ml-auto"])} size="sm">
                    {activeStep < 1 && "Próximo"}
                    {activeStep === 1 && "Revisar"}
                  </Button>
                )}

                {activeStep === 2 && (
                  <Button type="submit" size="sm" className="!uppercase" loading={methods.formState.isSubmitting}>
                    Concluir
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </Block.Form>
      </SideBySide.Primary>

      <SideBySide.Secondary>
        {companyLogo && <img src={companyLogo} className={"w-[400px] xl:w-[550px]"} />}
        {!companyLogo && <Loader className={hiddenOnMobile({ className: "mb-2" })} />}
      </SideBySide.Secondary>
    </>
  );
}

const hiddenOnMobile = cva({
  base: "block md:hidden"
});

const Info = {
  Default: InfoDefault,
  Color: InfoColor
};

type InfoProps = OptionData & ComponentProps<"div">;
function InfoDefault({ label, value, className, ...props }: InfoProps) {
  return (
    <div className={cn([labelWrapper, className])} {...props}>
      <label className={labelStyle()}>{label}:</label>
      {value && <p className="block">{value}</p>}
      {!value && <p className="block text-center">-----</p>}
    </div>
  );
}

function InfoColor({ label, value, className, ...props }: InfoProps) {
  return (
    <div className={cn([labelWrapper, className])} {...props}>
      <label className={labelStyle()}>{label}</label>
      {value && <div className="block size-6 w-full rounded-sm shadow" style={{ backgroundColor: value as string }}></div>}
      {!value && <p className="block text-center">-----</p>}
    </div>
  );
}
const labelWrapper = cn("flex-[1_1_min(100%_,_125px)]");
const labelStyle = cva({
  base: "text-primary mb-2 font-bold",
  variants: {
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg"
    }
  },
  defaultVariants: {
    size: "md"
  }
});

const Step = {
  One: StepOne,
  Two: StepTwo,
  Three: StepThree,
  Four: StepFour
};

function StepOne() {
  const { control } = useFormContext<Schema>();

  return (
    <div className="flex flex-col gap-4">
      <CustomInput.Text placeholder="Nome completo" leftSection={<UserIcon weight="bold" />} name="user.name" control={control} autoFocus />

      <CustomInput.Text maskProps={{ mask: "999.999.999-99" }} placeholder="CPF" leftSection={<UserIcon weight="bold" />} name="user.cpf" control={control} />

      <CustomInput.Text maskProps={{ mask: "99999999999" }} placeholder="CNH" leftSection={<UserIcon weight="bold" />} name="user.cnh" control={control} />

      <CustomInput.Text
        maskProps={{ mask: "99/99/9999" }}
        placeholder="Data de nascimento"
        leftSection={<CalendarBlankIcon weight="bold" />}
        name="user.birthDate"
        control={control}
      />

      <CustomInput.Text maskProps={{ mask: "(99) 9 9999-9999" }} leftSection={<PhoneIcon weight="bold" />} placeholder="Telefone" name="user.phone" control={control} />

      <CustomInput.Text name="user.email" leftSection={<EnvelopeIcon weight="bold" />} placeholder="E-mail" control={control} />
    </div>
  );
}

type StepTwoProps = {
  brands: { data: OptionData[]; isLoading: boolean; isError: boolean };
  models: { data: OptionData[]; isLoading: boolean; isError: boolean };
  implementsTypes: OptionData[];
};
function StepTwo({ models, brands, implementsTypes }: StepTwoProps) {
  return <Fields.Truck brands={brands} models={models} implementsTypes={implementsTypes} />;
}

type StepThreeProps = {
  brandLabel: string;
  modelLabel: string;
};
function StepThree({ brandLabel, modelLabel }: StepThreeProps) {
  const { getValues } = useFormContext<Schema>();
  const values = getValues();

  return (
    <>
      <Fieldset legend="Dados pessoais:" className="mb-2 flex flex-wrap gap-2 md:gap-3">
        <Info.Default label="Nome" value={values.user.name} />

        <Info.Default label="CPF" value={values.user.cpf} />

        <Info.Default label="CNH" value={values.user.cnh} />

        <Info.Default label="Data Nascimento" value={values.user.birthDate} />

        <Info.Default label="Fone" value={values.user.phone} />

        <Info.Default label="Email" value={values.user.email} />
      </Fieldset>

      <Fieldset legend="Dados do Caminhão:" className="flex flex-wrap gap-2 md:gap-3">
        <Info.Default label="Placa" value={values.truck.plate} />

        <Info.Default label="Marca" value={brandLabel} />

        <Info.Default label="Modelo" value={modelLabel} />

        <Info.Color label="Cor" value={values.truck.color} />

        <Info.Default label="Renavam" value={values.truck.renavam} />

        <Info.Default label="Tipo de Implemento" value={values.truck.implementType} />

        <Info.Default label="Qnt. de eixos" value={String(values.truck.shaftQuantity)} />

        <Info.Default label="Capacidade bruta" value={`${values.truck.grossCapacity}t`} />

        <Info.Default label="Capacidade líquida" value={`${values.truck.netCapacity}t`} />
      </Fieldset>
    </>
  );
}

type StepFourProps = {
  success: boolean;
  error: boolean;
};
function StepFour({ success, error }: StepFourProps) {
  const navigate = useNavigate();
  const {
    formState: { isSubmitting, isSubmitSuccessful },
    getValues
  } = useFormContext<Schema>();

  const cpf = getValues("user.cpf");
  const criptedCpf = crypto.encrypt(cpf);
  const birthday = getValues("user.birthDate");
  const criptedBirthday = crypto.encrypt(birthday);

  const handleSuccess = () => {
    navigate({
      to: "/app/login",
      search: {
        cpf: criptedCpf,
        birthDate: criptedBirthday
      }
    });
  };

  const eventListeners: EventListener[] = [
    {
      eventName: "complete",
      callback: handleSuccess
    }
  ];

  if (isSubmitting) {
    return (
      <div className="flex items-center justify-center py-16 md:py-32">
        <Loader size="xl" />
      </div>
    );
  }

  if (!isSubmitSuccessful || !success || error) return <LErrorIcon />;

  return <LSuccessIcon options={{ loop: false }} eventListeners={eventListeners} />;
}
